import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { BRAND } from "../lib/brand";
import {
  ArrowRight, Leaf, ShieldCheck, Package, ChartLineUp, Barcode, Factory,
  Phone, EnvelopeSimple, MapPin, Clock, InstagramLogo, FacebookLogo,
  WhatsappLogo, CheckCircle, List, X, Storefront,
} from "@phosphor-icons/react";

// -----------------------------------------------------------------------
// Content — swap freely from a single place
// -----------------------------------------------------------------------
const BRAND_NAME = "EATH'S";
const PLANT_NAME = "Sneha Foods";
const TAGLINE = " Crunch That Cares ❤️.";
const HERO_SUB =
  "Handcrafted snacks — potato chips, banana chips, and traditional namkeens — produced in a modern, FIFO-tracked facility that never compromises on freshness.";

const HERO_IMG =
  "https://images.unsplash.com/photo-1613919113640-25732ec5e61f?auto=format&fit=crop&w=2000&q=80";
const ABOUT_IMG =
  "https://images.unsplash.com/photo-1551884170-09fb70a3a2ed?auto=format&fit=crop&w=1600&q=80";
const PROCESS_IMG =
  "https://images.pexels.com/photos/24394699/pexels-photo-24394699.jpeg?auto=compress&cs=tinysrgb&w=1600";
const QUALITY_IMG =
  "https://images.unsplash.com/photo-1700727448575-6f1680cd7d75?auto=format&fit=crop&w=1600&q=80";

const PRODUCTS = [
  {
    name: "Classic Potato Chips",
    tag: "Signature",
    desc: "Hand-slicked potatoes, kettle-fried in small batches for the perfect crunch.",
    img: "https://images.pexels.com/photos/17480807/pexels-photo-17480807.jpeg?auto=compress&cs=tinysrgb&w=900",
  },
  {
    name: "Banana Chips",
    tag: "Kerala style",
    desc: "Raw banana, coconut oil, and a whisper of salt. Nothing else — the way it should be.",
    img: "https://images.pexels.com/photos/5992272/pexels-photo-5992272.jpeg?auto=compress&cs=tinysrgb&w=900",
  },
  {
    name: "Namkeen Mixture",
    tag: "Traditional",
    desc: "A crunchy blend of gram flour sev, peanuts and lentils, seasoned with heirloom spices.",
    img: "https://images.pexels.com/photos/13220344/pexels-photo-13220344.jpeg?auto=compress&cs=tinysrgb&w=900",
  },
  {
    name: "Roasted Chana",
    tag: "Guilt-free",
    desc: "Slow-roasted chickpeas with a hit of black salt and jeera. High protein, zero oil.",
    img: "https://images.pexels.com/photos/17480807/pexels-photo-17480807.jpeg?auto=compress&cs=tinysrgb&w=900&sat=-30",
  },
  {
    name: "Bhujia Sev",
    tag: "Bikaner style",
    desc: "Fine sev with a hint of ajwain — a tea-time staple with real character.",
    img: "https://images.pexels.com/photos/5992272/pexels-photo-5992272.jpeg?auto=compress&cs=tinysrgb&w=900&sat=-20",
  },
  {
    name: "Masala Peanuts",
    tag: "Bold",
    desc: "Whole peanuts wrapped in a spiced batter and roasted crisp. Weekend approved.",
    img: "https://images.pexels.com/photos/13220344/pexels-photo-13220344.jpeg?auto=compress&cs=tinysrgb&w=900&sat=-15",
  },
];

const CAPABILITIES = [
  {
    icon: Leaf,
    title: "Farm-fresh sourcing",
    body: "Direct from farmers. No middlemen — the raw material reaches our plant.",
  },
  {
    icon: ShieldCheck,
    title: "Food-safe manufacturing",
    body: "Stainless-steel production line, hairnets and gloves policy, and daily hygiene audits by our floor supervisor.",
  },
  {
    icon: Barcode,
    title: "Batch traceability",
    body: "Every bag we produce carries a barcode that traces back to the exact raw-material lot and shift.",
  },
  {
    icon: Package,
    title: "FIFO inventory",
    body: "First-in, first-out on every ingredient — no bag sits older than 30 days on our floor.",
  },
  {
    icon: ChartLineUp,
    title: "Data-driven quality",
    body: "Live dashboards on yield, wastage and quality issues — problems are caught before they ship.",
  },
  {
    icon: Factory,
    title: "Scalable capacity",
    body: "1.2 tonnes/day baseline capacity, scalable to 3 tonnes for seasonal demand and B2B orders.",
  },
];

const STATS = [
  { value: "2026", label: "Since" },
  { value: "20+", label: "SKUs" },
  { value: "100+", label: "Distributors" },
  { value: "17+", label: "Cities Serving" },
];

const CERTS = [
  { title: "FSSAI Certified", note: "Licensed" },
  { title: "GST Registered", note: "GSTIN available on invoice" },
  { title: "HACCP Aligned", note: "Hazard-critical control points enforced" },
  { title: "Andhra Pradesh MSME", note: "Registered under UDYAM" },
];

const CONTACT = {
  address: "SNEHA FOODS,Block F ,56/A, Industrial Development Area,Autonagar, Andhra Pradesh, India - 530012",
  phone: "+91 7893304411",
  email: "customer@snehafoods.in",
  hours: "Mon – Sat · 9:00 AM to 5:00 PM",
  instagram: "https://www.instagram.com/eat_eaths?igsh=MXlibGg5NjF1bml0&utm_source=qr.",
  facebook: "https://facebook.com/eaths.snacks",
  whatsapp: "https://wa.me/917893304411",
};

// -----------------------------------------------------------------------

export default function Landing() {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white text-zinc-900 font-sans antialiased">
      {/* NAV */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur bg-white/85 border-b border-zinc-200">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 lg:px-8 h-16">
          <a href="#top" className="flex items-center gap-3">
            <img src={BRAND.logo} alt="EATH's" className="h-9 w-9 object-contain" />
            <div className="leading-none">
              <div className="brand-font text-xl leading-none normal-case">{BRAND_NAME}</div>
              <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500">by {PLANT_NAME}</div>
            </div>
          </a>

          <div className="hidden md:flex items-center gap-8 text-sm font-medium">
            <a href="#products" className="hover:text-orange-600 transition-colors">Products</a>
            <a href="#story" className="hover:text-orange-600 transition-colors">Our Story</a>
            <a href="#capabilities" className="hover:text-orange-600 transition-colors">Manufacturing</a>
            <a href="#certifications" className="hover:text-orange-600 transition-colors">Certifications</a>
            <a href="#contact" className="hover:text-orange-600 transition-colors">Contact</a>
          </div>

          <div className="flex items-center gap-2">
            <Link
              to="/dashboard"
              data-testid="nav-crm-btn"
              className="hidden sm:inline-flex items-center gap-2 bg-zinc-900 hover:bg-orange-600 text-white text-xs font-bold uppercase tracking-widest px-4 py-2.5 transition-colors"
            >
              Team Login <ArrowRight size={14} weight="bold" />
            </Link>
            <button
              className="md:hidden inline-flex items-center justify-center w-10 h-10 border border-zinc-300"
              onClick={() => setMobileOpen(v => !v)}
              aria-label="Menu"
            >
              {mobileOpen ? <X size={18} weight="bold" /> : <List size={18} weight="bold" />}
            </button>
          </div>
        </div>
        {mobileOpen && (
          <div className="md:hidden border-t border-zinc-200 bg-white">
            <div className="flex flex-col p-4 gap-3 text-sm font-medium">
              <a onClick={() => setMobileOpen(false)} href="#products">Products</a>
              <a onClick={() => setMobileOpen(false)} href="#story">Our Story</a>
              <a onClick={() => setMobileOpen(false)} href="#capabilities">Manufacturing</a>
              <a onClick={() => setMobileOpen(false)} href="#certifications">Certifications</a>
              <a onClick={() => setMobileOpen(false)} href="#contact">Contact</a>
              <Link to="/dashboard" className="bg-zinc-900 text-white text-center py-3 font-bold uppercase text-xs tracking-widest">
                Team Login →
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* HERO */}
      <section id="top" className="relative pt-16 min-h-screen flex items-center overflow-hidden bg-zinc-950">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="EATH's snacks" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-br from-zinc-950 via-zinc-950/70 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 lg:px-8 py-24 lg:py-32 grid lg:grid-cols-12 gap-8 text-white">
          <div className="lg:col-span-8">
            <div className="inline-flex items-center gap-2 border border-orange-500/40 bg-orange-500/10 text-orange-300 px-3 py-1 text-[11px] font-mono uppercase tracking-widest mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-orange-400 animate-pulse" />
              Manufactured at {PLANT_NAME} · Andhra Pradesh
            </div>
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-black tracking-tight leading-[0.95] mb-6">
              {TAGLINE.split(".").map((line, i, arr) =>
                line.trim() ? (
                  <span key={i} className="block">
                    {line.trim()}
                    {i < arr.length - 2 ? "." : ""}
                  </span>
                ) : null
              )}
            </h1>
            <p className="text-lg md:text-xl text-zinc-300 max-w-2xl mb-8 leading-relaxed">
              {HERO_SUB}
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                href="#products"
                className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-black font-bold px-6 py-3.5 uppercase tracking-widest text-sm transition-colors"
              >
                Explore Products <ArrowRight size={16} weight="bold" />
              </a>
            </div>
            <div className="mt-10 flex flex-wrap gap-6 text-[11px] font-mono uppercase tracking-widest text-zinc-400">
              <span>FSSAI Certified</span>
              <span>·</span>
              <span>GST Registered</span>
              <span>·</span>
              <span>Farm-Fresh Sourcing</span>
              <span>·</span>
              <span>Barcode Traceability</span>
            </div>
          </div>
        </div>
      </section>

      {/* STATS STRIP */}
      <section className="bg-orange-500 text-black">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {STATS.map(s => (
            <div key={s.label} className="text-center md:text-left">
              <div className="text-4xl md:text-5xl font-black tracking-tight">{s.value}</div>
              <div className="text-[11px] font-mono uppercase tracking-widest opacity-70">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* PRODUCTS */}
      <section id="products" className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <SectionHeader
            eyebrow="Our Range"
            title="Snacks with soul."
            subtitle="Six flagship SKUs and 20+ variants — every recipe developed in-house, tested by our team, and produced in-plant."
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            {PRODUCTS.map(p => (
              <article
                key={p.name}
                className="group relative bg-white border border-zinc-200 hover:border-orange-500 transition-colors duration-200 overflow-hidden"
              >
                <div className="aspect-[4/3] overflow-hidden bg-zinc-100">
                  <img
                    src={p.img}
                    alt={p.name}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-xl font-black tracking-tight">{p.name}</h3>
                    <span className="text-[10px] font-mono uppercase tracking-widest bg-orange-100 text-orange-700 px-2 py-1">
                      {p.tag}
                    </span>
                  </div>
                  <p className="text-sm text-zinc-600 leading-relaxed">{p.desc}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* STORY (About) */}
      <section id="story" className="py-24 lg:py-32 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <SectionHeader
              eyebrow="Our Story"
              title={`Built at ${PLANT_NAME}. Loved as ${BRAND_NAME}.`}
              align="left"
            />
            <div className="space-y-4 text-zinc-700 leading-relaxed mt-6 text-lg">
              <p>
                {PLANT_NAME} started as a small family kitchen in Visakhapatnam with one obsession —
                snacks that taste like they were made at home, but at scale. Today, we run a
                modern manufacturing facility that produces the {BRAND_NAME} brand you know and love.
              </p>
              <p>
                We source raw materials directly from farmers, we track every kilogram
                through our production floor with a barcode system we built ourselves, and we
                run the cleanest kettle-fry line in the region.
              </p>
              <p>
                We&apos;re not the biggest snack brand in India. We&apos;re just the one that hasn&apos;t
                cut a single corner since 2026.
              </p>
            </div>
            <Link
              to="/dashboard"
              className="mt-8 inline-flex items-center gap-2 text-orange-600 hover:text-orange-700 font-bold uppercase tracking-widest text-sm"
            >
              See our live operations dashboard <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
          <div className="relative">
            <img
              src={ABOUT_IMG}
              alt={`Inside ${PLANT_NAME}`}
              className="w-full aspect-[4/5] object-cover shadow-2xl"
            />
            <div className="absolute -bottom-6 -left-6 bg-orange-500 text-black p-6 shadow-lg max-w-xs hidden md:block">
              <div className="text-3xl font-black">100%</div>
              <div className="text-xs font-mono uppercase tracking-widest mt-1 leading-tight">
                In-house production. No third-party co-packing, ever.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section id="capabilities" className="py-24 lg:py-32 bg-zinc-950 text-white">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <SectionHeader
            eyebrow="Manufacturing"
            title="Six things we refuse to compromise on."
            subtitle="The invisible details that separate a good snack from a great one."
            dark
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            {CAPABILITIES.map(c => (
              <div
                key={c.title}
                className="border border-zinc-800 hover:border-orange-500 p-8 transition-colors duration-200 group bg-zinc-900/40"
              >
                <c.icon size={32} weight="duotone" className="text-orange-500 mb-4" />
                <h3 className="text-xl font-black tracking-tight mb-3">{c.title}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{c.body}</p>
              </div>
            ))}
          </div>

          <div className="mt-16 grid lg:grid-cols-2 gap-8">
            <img src={PROCESS_IMG} alt="Production floor" className="w-full aspect-video object-cover" />
            <img src={QUALITY_IMG} alt="Quality control" className="w-full aspect-video object-cover" />
          </div>
        </div>
      </section>

      {/* CERTIFICATIONS */}
      <section id="certifications" className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <SectionHeader
            eyebrow="Trust & Compliance"
            title="Certified where it matters."
            subtitle="Every ingredient, every batch, every shift — documented and audit-ready."
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {CERTS.map(c => (
              <div key={c.title} className="border border-zinc-200 p-6 bg-white text-center">
                <CheckCircle size={32} weight="fill" className="text-green-600 mx-auto mb-3" />
                <div className="font-black tracking-tight text-lg">{c.title}</div>
                <div className="text-xs text-zinc-500 mt-2 font-mono uppercase tracking-wider">{c.note}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MARQUEE STRIP */}
      <section aria-hidden="true">
        <div
          className="border-y overflow-hidden"
          style={{ background: "#d47a4b", color: "#f5f0e1", borderColor: "#c96a3a" }}
        >
          <div className="marquee-track flex whitespace-nowrap py-4 text-lg italic font-black tracking-wide">
            {Array.from({ length: 2 }).map((_, k) => (
              <div key={k} className="flex items-center gap-8 pr-8">
                {[
                  "Real Crunch",
                  "Real Taste",
                  "Made for Every You",
                  "No Palm Oil",
                  "No Preservatives",
                  "Snack Happy All Day",
                ].map((t, i) => (
                  <span key={`${k}-${i}`} className="inline-flex items-center gap-6">
                    <span>{t}</span>
                    <span
                      className="inline-block"
                      style={{
                        width: 14, height: 14,
                        background: "#f89f57",
                        clipPath: "polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)",
                      }}
                    />
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <SectionHeader
            eyebrow="Get in touch"
            title="Distributor, retailer, or curious foodie — say hi."
            subtitle="Whether you're a distributor exploring bulk orders, or a consumer please reach out to us. It’s our pleasure to help, and we always reply within one working day."
          />

          <div className="mt-12 grid lg:grid-cols-2 gap-12">
            <div className="space-y-6">
              <InfoRow icon={MapPin} label="Manufacturing Unit" value={CONTACT.address} />
              <InfoRow icon={Phone} label="Phone" value={CONTACT.phone} href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} />
              <InfoRow icon={EnvelopeSimple} label="Email" value={CONTACT.email} href={`mailto:${CONTACT.email}`} />
              <InfoRow icon={Clock} label="Working hours" value={CONTACT.hours} />

              <div className="pt-6 border-t border-zinc-200">
                <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mb-3">Follow along</div>
                <div className="flex items-center gap-3">
                  <SocialLink href={CONTACT.instagram} icon={InstagramLogo} label="Instagram" />
                  <SocialLink href={CONTACT.facebook} icon={FacebookLogo} label="Facebook" />
                  <SocialLink href={CONTACT.whatsapp} icon={WhatsappLogo} label="WhatsApp" />
                </div>
              </div>
            </div>

            <ContactForm />
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-black text-zinc-400 py-12">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 grid md:grid-cols-3 gap-8">
          <div>
            <div className="flex items-center gap-3">
              <img src={BRAND.logo} alt="EATH's" className="h-10 w-10 object-contain" />
              <div>
                <div className="brand-font text-white text-2xl leading-none normal-case">{BRAND_NAME}</div>
                <div className="text-[10px] font-mono uppercase tracking-widest">by {PLANT_NAME}</div>
              </div>
            </div>
            <p className="text-xs mt-4 max-w-xs leading-relaxed">
              Handcrafted Indian snacks, manufactured with modern discipline. Andhra Pradesh, India.
            </p>
          </div>
          <div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-white mb-3">Explore</div>
            <ul className="space-y-2 text-sm">
              <li><a href="#products" className="hover:text-orange-400">Products</a></li>
              <li><a href="#story" className="hover:text-orange-400">Our Story</a></li>
              <li><a href="#capabilities" className="hover:text-orange-400">Manufacturing</a></li>
              <li><a href="#contact" className="hover:text-orange-400">Contact</a></li>
            </ul>
          </div>
          <div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-white mb-3">Team</div>
            <ul className="space-y-2 text-sm">
              <li><Link to="/login" className="hover:text-orange-400">Team Login → </Link></li>
            </ul>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 lg:px-8 mt-10 pt-6 border-t border-zinc-800 flex flex-wrap justify-between gap-4 text-[11px] font-mono uppercase tracking-widest">
          <span>© {new Date().getFullYear()} {PLANT_NAME}. All rights reserved.</span>
          <span>snehafoods.in</span>
        </div>
      </footer>
    </div>
  );
}

function SectionHeader({ eyebrow, title, subtitle, align = "center", dark = false }) {
  const isCenter = align === "center";
  return (
    <div className={isCenter ? "text-center max-w-3xl mx-auto" : ""}>
      <div className={`text-[11px] font-mono uppercase tracking-widest mb-3 ${dark ? "text-orange-400" : "text-orange-600"}`}>
        {eyebrow}
      </div>
      <h2 className={`text-4xl md:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] ${dark ? "text-white" : "text-zinc-900"}`}>
        {title}
      </h2>
      {subtitle && (
        <p className={`mt-4 text-lg leading-relaxed ${dark ? "text-zinc-400" : "text-zinc-600"}`}>{subtitle}</p>
      )}
    </div>
  );
}

function InfoRow({ icon: Icon, label, value, href }) {
  const inner = (
    <div className="flex gap-4">
      <div className="shrink-0 w-11 h-11 rounded-full bg-orange-100 flex items-center justify-center">
        <Icon size={18} weight="bold" className="text-orange-600" />
      </div>
      <div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{label}</div>
        <div className="text-base font-medium text-zinc-900 mt-1">{value}</div>
      </div>
    </div>
  );
  return href ? <a href={href} className="block hover:opacity-80">{inner}</a> : inner;
}

function SocialLink({ href, icon: Icon, label }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      aria-label={label}
      className="w-10 h-10 border border-zinc-300 hover:border-orange-500 hover:text-orange-600 flex items-center justify-center transition-colors"
    >
      <Icon size={18} weight="bold" />
    </a>
  );
}

function ContactForm() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    is_distributor: false,
    firm_name: "", products_sold: "", location: "", coverage: "",
    message: "",
  });
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [errorMsg, setErrorMsg] = useState("");

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setErrorMsg("");
    try {
      await api.post("/contact", form);
      setStatus("sent");
      setForm({
        name: "", email: "", phone: "",
        is_distributor: false,
        firm_name: "", products_sold: "", location: "", coverage: "",
        message: "",
      });
    } catch (err) {
      setStatus("error");
      setErrorMsg(err.response?.data?.detail || "Something went wrong. Please try again.");
    }
  };

  if (status === "sent") {
    return (
      <div className="bg-green-50 border-2 border-green-600 p-10 text-center" data-testid="contact-success">
        <CheckCircle size={48} weight="fill" className="text-green-600 mx-auto mb-4" />
        <h3 className="text-2xl font-black tracking-tight mb-2">Thanks! Message received.</h3>
        <p className="text-zinc-600 leading-relaxed">
          We&apos;ve got your enquiry and our team will reach out within one working day.
          {form.is_distributor ? " Distributor enquiries are prioritised." : ""}
        </p>
        <button
          onClick={() => setStatus("idle")}
          className="mt-6 text-orange-600 hover:text-orange-700 font-bold uppercase tracking-widest text-sm"
        >
          Send another message →
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-zinc-50 border border-zinc-200 p-8 space-y-5" data-testid="contact-form">
      {/* Distributor toggle */}
      <div className="border-b border-zinc-200 pb-5">
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-3">Are you a distributor / retailer?</div>
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => setForm({ ...form, is_distributor: true })}
            data-testid="distributor-yes"
            className={`p-4 border-2 text-left transition-colors duration-200 ${
              form.is_distributor
                ? "border-orange-500 bg-orange-50"
                : "border-zinc-200 bg-white hover:border-zinc-400"
            }`}
          >
            <Storefront size={20} weight="bold" className={form.is_distributor ? "text-orange-600 mb-2" : "text-zinc-500 mb-2"} />
            <div className="font-black text-sm">Yes, distributor</div>
            <div className="text-[11px] text-zinc-500 mt-0.5">Bulk / wholesale enquiry</div>
          </button>
          <button
            type="button"
            onClick={() => setForm({ ...form, is_distributor: false, firm_name: "", products_sold: "", location: "", coverage: "" })}
            data-testid="distributor-no"
            className={`p-4 border-2 text-left transition-all ${
              !form.is_distributor
                ? "border-orange-500 bg-orange-50"
                : "border-zinc-200 bg-white hover:border-zinc-400"
            }`}
          >
            <EnvelopeSimple size={20} weight="bold" className={!form.is_distributor ? "text-orange-600 mb-2" : "text-zinc-500 mb-2"} />
            <div className="font-black text-sm">General enquiry</div>
            <div className="text-[11px] text-zinc-500 mt-0.5">Question, feedback, other</div>
          </button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <FormField label={form.is_distributor ? "Your name (contact person)" : "Your name"}>
          <input
            required value={form.name} onChange={update("name")}
            className="w-full border border-zinc-300 px-4 py-3 focus:outline-none focus:border-orange-500"
            placeholder="Ramesh Kumar"
            data-testid="contact-name"
          />
        </FormField>
        <FormField label="Phone / WhatsApp">
          <input
            required value={form.phone} onChange={update("phone")}
            className="w-full border border-zinc-300 px-4 py-3 focus:outline-none focus:border-orange-500"
            placeholder="+91 90000 00000"
            data-testid="contact-phone"
          />
        </FormField>
      </div>

      <FormField label="Email">
        <input
          type="email" required value={form.email} onChange={update("email")}
          className="w-full border border-zinc-300 px-4 py-3 focus:outline-none focus:border-orange-500"
          placeholder="you@company.com"
          data-testid="contact-email"
        />
      </FormField>

      {form.is_distributor && (
        <div className="space-y-4 border-t border-zinc-200 pt-5" data-testid="distributor-fields">
          <div className="text-[10px] font-mono uppercase tracking-widest text-orange-600">Distributor details</div>

          <FormField label="Firm / Company name">
            <input
              required value={form.firm_name} onChange={update("firm_name")}
              className="w-full border border-zinc-300 px-4 py-3 focus:outline-none focus:border-orange-500"
              placeholder="e.g. Ramesh Traders Pvt. Ltd."
              data-testid="contact-firm"
            />
          </FormField>

          <div className="grid sm:grid-cols-2 gap-4">
            <FormField label="City / State (based in)">
              <input
                required value={form.location} onChange={update("location")}
                className="w-full border border-zinc-300 px-4 py-3 focus:outline-none focus:border-orange-500"
                placeholder="e.g. Visakhapatnam"
                data-testid="contact-location"
              />
            </FormField>
            <FormField label="Areas you cover">
              <input
                value={form.coverage} onChange={update("coverage")}
                className="w-full border border-zinc-300 px-4 py-3 focus:outline-none focus:border-orange-500"
                placeholder="e.g. South Bengaluru, Mysuru"
                data-testid="contact-coverage"
              />
            </FormField>
          </div>

          <FormField label="What products do you currently sell?">
            <input
              value={form.products_sold} onChange={update("products_sold")}
              className="w-full border border-zinc-300 px-4 py-3 focus:outline-none focus:border-orange-500"
              placeholder="e.g. Snacks, chips, biscuits, FMCG"
              data-testid="contact-products"
            />
          </FormField>
        </div>
      )}

      <FormField label={form.is_distributor ? "Any additional info (order volume, timeline, etc.)" : "How can we help?"}>
        <textarea
          rows={4}
          required={!form.is_distributor}
          value={form.message} onChange={update("message")}
          className="w-full border border-zinc-300 px-4 py-3 focus:outline-none focus:border-orange-500 resize-none"
          placeholder={form.is_distributor
            ? "Expected monthly volume, preferred SKUs, timeline…"
            : "Tell us what you're looking for…"}
          data-testid="contact-message"
        />
      </FormField>

      {status === "error" && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 p-3" data-testid="contact-error">
          {errorMsg}
        </div>
      )}

      <button
        type="submit"
        disabled={status === "sending"}
        className="w-full bg-zinc-900 hover:bg-orange-600 text-white font-bold uppercase tracking-widest text-sm py-4 transition-colors disabled:opacity-60"
        data-testid="contact-submit"
      >
        {status === "sending"
          ? "Sending…"
          : form.is_distributor ? "Submit Distributor Enquiry" : "Send Message"}
      </button>
      <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 text-center">
        Or reach us directly at {CONTACT.phone}
      </p>
    </form>
  );
}

function FormField({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-2">{label}</span>
      {children}
    </label>
  );
}
